import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReactFlow, {
  Background,
  Controls,
  MarkerType,
  MiniMap,
  Panel,
  ReactFlowProvider,
  useEdgesState,
  useNodesState,
  useReactFlow
} from 'reactflow';
import 'reactflow/dist/style.css';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Snackbar,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography
} from '@mui/material';
import AddLinkIcon from '@mui/icons-material/AddLink';
import CenterFocusStrongIcon from '@mui/icons-material/CenterFocusStrong';
import RefreshIcon from '@mui/icons-material/Refresh';
import SemanticTableService from '../../services/SemanticTableService';
import ConfirmDestructiveDialog from '../common/ConfirmDestructiveDialog';
import ModelTableNode from './ModelTableNode';
import ModelGraphDetailsPanel from './ModelGraphDetailsPanel';
import JoinEditorDialog from './JoinEditorDialog';
import EntityEditDialog from './EntityEditDialog';
import {
  apiErrorMessage,
  buildModelGraph,
  layoutModelGraph,
  loadModelGraphSource,
  tableLabel
} from './modelGraphData';

const ACCENT = 'var(--wb-accent)';
const MUTED = 'var(--wb-muted-75)';
const BORDER = 'var(--wb-border)';

const nodeTypes = { modelTable: ModelTableNode };

const baseEdgeStyle = { stroke: 'var(--wb-x-text-94a3b88c, rgba(148, 163, 184, 0.55))', strokeWidth: 1.5 };
const activeEdgeStyle = { stroke: ACCENT, strokeWidth: 2.5 };

const toggleSx = {
  color: MUTED,
  borderColor: 'var(--wb-x-border-94a3b840, rgba(148, 163, 184, 0.25))',
  textTransform: 'none',
  px: 1.25,
  py: 0.25,
  '&.Mui-selected': { color: ACCENT, backgroundColor: 'var(--wb-accent-tint)' },
  '&.Mui-selected:hover': { backgroundColor: 'var(--wb-x-bg-0ea5e938, rgba(14, 165, 233, 0.22))' }
};
const toolbarButtonSx = { textTransform: 'none', color: 'var(--wb-text)', borderColor: 'var(--wb-x-border-94a3b840, rgba(148, 163, 184, 0.25))' };

/** Aplica estilos de resaltado según lo seleccionado. */
const decorateEdges = (edges, selection) => edges.map((edge) => {
  const active = selection && (
    (selection.type === 'edge' && selection.id === edge.id)
    || (selection.type === 'table' && (edge.source === `tbl-${selection.table.table_id}` || edge.target === `tbl-${selection.table.table_id}`))
  );
  return {
    ...edge,
    animated: Boolean(active),
    style: active ? activeEdgeStyle : baseEdgeStyle,
    markerEnd: { type: MarkerType.ArrowClosed, color: active ? ACCENT : 'var(--wb-muted-70)', width: 16, height: 16 },
    labelStyle: { fill: active ? ACCENT : 'var(--wb-x-text-cbd5e1, #cbd5e1)', fontSize: 11, fontWeight: 600 },
    labelBgStyle: { fill: 'var(--wb-bg-surface)' },
    labelBgPadding: [4, 2],
    labelBgBorderRadius: 4
  };
});

const DiagramCanvas = ({ modelId }) => {
  const { fitView } = useReactFlow();
  const [status, setStatus] = useState({ loading: true, error: null });
  const [graph, setGraph] = useState({ nodes: [], edges: [], externalJoins: [], missingTableIds: [] });
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [direction, setDirection] = useState('LR');
  const [selection, setSelection] = useState(null);
  // Tablas del modelo con sus joins: fuente de verdad del diagrama; crear,
  // editar o borrar una relación las actualiza y el grafo se rearma.
  const [tables, setTables] = useState([]);
  // { mode: 'create' | 'edit', initial: { sourceTableId, targetTableId, join? } }
  const [joinEditor, setJoinEditor] = useState(null);
  const [pendingDeleteJoin, setPendingDeleteJoin] = useState(null); // datos de la arista
  const [notice, setNotice] = useState(null); // { message, severity }
  const [editingTable, setEditingTable] = useState(null); // entidad abierta en el formulario

  // fitView cambia de identidad cuando React Flow termina de inicializarse;
  // se lee desde una ref para que eso no vuelva a disparar la carga.
  const fitViewRef = useRef(fitView);
  fitViewRef.current = fitView;
  const directionRef = useRef(direction);
  directionRef.current = direction;

  const applyLayout = useCallback((baseNodes, baseEdges, layoutDirection) => {
    setNodes(layoutModelGraph(baseNodes, baseEdges, layoutDirection));
    // fitView necesita los nodos ya medidos: se pide en el próximo frame.
    window.requestAnimationFrame(() => fitViewRef.current({ padding: 0.15, duration: 300 }));
  }, [setNodes]);

  const load = useCallback(async () => {
    setStatus({ loading: true, error: null });
    setSelection(null);
    try {
      const source = await loadModelGraphSource(modelId);
      setTables(source.tables);
      const built = buildModelGraph(source.tables);
      setGraph({ ...built, missingTableIds: source.missingTableIds });
      setEdges(built.edges);
      applyLayout(built.nodes, built.edges, directionRef.current);
      setStatus({ loading: false, error: null });
    } catch (error) {
      console.error('Error cargando el diagrama del modelo:', error);
      setStatus({ loading: false, error: error.message || 'No se pudo cargar el modelo semántico.' });
    }
  }, [modelId, setEdges, applyLayout]);

  useEffect(() => {
    load();
  }, [load]);

  const handleDirection = (event, value) => {
    if (!value) return;
    setDirection(value);
    applyLayout(graph.nodes, graph.edges, value);
  };

  const selectTable = useCallback((table) => {
    setSelection({ type: 'table', table });
    setNodes((current) => current.map((node) => ({ ...node, selected: node.id === `tbl-${table.table_id}` })));
  }, [setNodes]);

  const decoratedEdges = useMemo(() => decorateEdges(edges, selection), [edges, selection]);

  /**
   * Rearma el grafo tras crear/editar/borrar una relación SIN mover las
   * tablas que ya estaban (el usuario pudo haberlas acomodado a mano); solo
   * las nuevas toman la posición del layout. "Reorganizar" re-acomoda todo.
   */
  const rebuildGraph = useCallback((nextTables) => {
    setTables(nextTables);
    const built = buildModelGraph(nextTables);
    setGraph((prev) => ({ ...prev, ...built }));
    setEdges(built.edges);
    setNodes((current) => {
      const previous = new Map(current.map((node) => [node.id, node]));
      return layoutModelGraph(built.nodes, built.edges, directionRef.current).map((node) => (
        previous.has(node.id)
          ? { ...node, position: previous.get(node.id).position, selected: previous.get(node.id).selected }
          : node
      ));
    });
    return built;
  }, [setEdges, setNodes]);

  const tableIdFromNode = (nodeId) => String(nodeId).replace(/^tbl-/, '');

  const handleConnect = useCallback((params) => {
    setJoinEditor({
      mode: 'create',
      initial: { sourceTableId: tableIdFromNode(params.source), targetTableId: tableIdFromNode(params.target) }
    });
  }, []);

  const saveJoin = async ({ sourceTable, payload, joinId }) => {
    let saved;
    try {
      saved = joinId
        ? await SemanticTableService.updateJoin(sourceTable.table_id, joinId, payload)
        : await SemanticTableService.addJoin(sourceTable.table_id, payload);
    } catch (error) {
      throw new Error(apiErrorMessage(error, 'No se pudo guardar la relación.'));
    }
    if (!saved) {
      // fetchWithAuth devuelve null en un 404.
      throw new Error('La tabla o la relación ya no existe. Recarga el diagrama.');
    }
    const savedJoin = { ...payload, ...saved, join_id: saved.join_id || joinId };
    const nextTables = tables.map((table) => {
      if (table.table_id !== sourceTable.table_id) return table;
      const joins = table.joins || [];
      return {
        ...table,
        joins: joinId
          ? joins.map((join) => (join.join_id === joinId ? savedJoin : join))
          : [...joins, savedJoin]
      };
    });
    const built = rebuildGraph(nextTables);
    const edge = built.edges.find((e) => e.id === `join-${sourceTable.table_id}-${savedJoin.join_id}`);
    setSelection(edge ? { type: 'edge', id: edge.id, data: edge.data } : null);
    setJoinEditor(null);
    setNotice({ message: joinId ? 'Relación actualizada.' : 'Relación creada.', severity: 'success' });
  };

  /**
   * Entidad guardada desde el formulario. Las relaciones de OTRAS tablas
   * apuntan a esta por su nombre físico (`target_table`): si cambió, se
   * actualizan para no perderlas.
   */
  const handleEntitySaved = async (updated, previous) => {
    let nextTables = tables.map((table) => (table.table_id === updated.table_id ? updated : table));
    const renamed = previous.table_name && updated.table_name && previous.table_name !== updated.table_name;
    let failedRenames = 0;
    if (renamed) {
      const results = await Promise.all(nextTables.flatMap((table) => (table.joins || [])
        .filter((join) => join.target_table === previous.table_name && join.join_id && table.table_id !== updated.table_id)
        .map((join) => SemanticTableService.updateJoin(table.table_id, join.join_id, { target_table: updated.table_name })
          .then(() => ({ tableId: table.table_id, joinId: join.join_id, ok: true }))
          .catch(() => ({ tableId: table.table_id, joinId: join.join_id, ok: false })))));
      failedRenames = results.filter((r) => !r.ok).length;
      const renamedKeys = new Set(results.filter((r) => r.ok).map((r) => `${r.tableId}:${r.joinId}`));
      nextTables = nextTables.map((table) => ({
        ...table,
        joins: (table.joins || []).map((join) => (
          renamedKeys.has(`${table.table_id}:${join.join_id}`) ? { ...join, target_table: updated.table_name } : join
        ))
      }));
    }
    rebuildGraph(nextTables);
    setSelection({ type: 'table', table: updated });
    setEditingTable(null);
    setNotice(failedRenames
      ? { message: `Entidad actualizada, pero ${failedRenames} relación(es) que apuntaban al nombre anterior no se pudieron actualizar.`, severity: 'warning' }
      : { message: 'Entidad actualizada.', severity: 'success' });
  };

  const deleteJoin = async () => {
    const { sourceTable, join } = pendingDeleteJoin;
    try {
      // null = 404: ya no existía; igual se quita del diagrama.
      await SemanticTableService.deleteJoin(sourceTable.table_id, join.join_id);
    } catch (error) {
      setNotice({ message: apiErrorMessage(error, 'No se pudo eliminar la relación.'), severity: 'error' });
      return;
    }
    rebuildGraph(tables.map((table) => (
      table.table_id === sourceTable.table_id
        ? { ...table, joins: (table.joins || []).filter((j) => j.join_id !== join.join_id) }
        : table
    )));
    setSelection(null);
    setPendingDeleteJoin(null);
    setNotice({ message: 'Relación eliminada.', severity: 'success' });
  };

  const tableCount = graph.nodes.length;

  return (
    <Box sx={{ display: 'flex', height: '100%', minHeight: 0 }}>
      <Box sx={{ flex: 1, minWidth: 0, position: 'relative' }}>
        {status.loading && (
          <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.5, zIndex: 5 }}>
            <CircularProgress size={24} sx={{ color: ACCENT }} />
            <Typography sx={{ color: MUTED }}>Cargando el modelo semántico…</Typography>
          </Box>
        )}
        {status.error && (
          <Box sx={{ p: 3 }}>
            <Alert severity="error" action={<Button color="inherit" size="small" onClick={load}>Reintentar</Button>}>
              {status.error}
            </Alert>
          </Box>
        )}
        {!status.loading && !status.error && tableCount === 0 && (
          <Box sx={{ p: 3 }}>
            <Alert severity="info">Este modelo todavía no tiene tablas. Agrégalas desde la vista de lista del modelo.</Alert>
          </Box>
        )}
        {!status.error && (
          <ReactFlow
            nodes={nodes}
            edges={decoratedEdges}
            nodeTypes={nodeTypes}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onNodeClick={(event, node) => selectTable(node.data.table)}
            onEdgeClick={(event, edge) => setSelection({ type: 'edge', id: edge.id, data: edge.data })}
            onPaneClick={() => setSelection(null)}
            // Arrastrar del borde de una tabla a otra crea una relación.
            nodesConnectable
            onConnect={handleConnect}
            isValidConnection={(connection) => connection.source !== connection.target}
            connectionLineStyle={{ stroke: ACCENT, strokeWidth: 2 }}
            minZoom={0.1}
            maxZoom={2}
            proOptions={{ hideAttribution: true }}
            style={{ backgroundColor: 'var(--wb-bg-page)' }}
          >
            <Background color="var(--wb-x-text-94a3b82e, rgba(148, 163, 184, 0.18))" gap={22} />
            <Controls showInteractive={false} />
            <MiniMap
              pannable
              zoomable
              nodeColor={() => 'rgba(56, 189, 248, 0.55)'}
              maskColor="rgba(2, 6, 23, 0.6)"
              style={{ backgroundColor: 'var(--wb-bg-panel)' }}
            />
            {tableCount > 0 && (
              <Panel position="top-left">
                <Box
                  sx={{
                    display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap',
                    p: 1, borderRadius: 2, backgroundColor: 'var(--wb-x-bg-121827eb, rgba(18, 24, 39, 0.92))', border: `1px solid ${BORDER}`
                  }}
                >
                  <Typography sx={{ color: MUTED, fontSize: '0.8rem', px: 0.5 }}>
                    {tableCount} tabla{tableCount === 1 ? '' : 's'} · {graph.edges.length} relaci{graph.edges.length === 1 ? 'ón' : 'ones'}
                  </Typography>
                  <ToggleButtonGroup size="small" exclusive value={direction} onChange={handleDirection} aria-label="Orientación">
                    <ToggleButton value="LR" sx={toggleSx}>Horizontal</ToggleButton>
                    <ToggleButton value="TB" sx={toggleSx}>Vertical</ToggleButton>
                  </ToggleButtonGroup>
                  <Tooltip title="También puedes arrastrar desde el borde de una tabla hasta otra">
                    <Button size="small" variant="contained" startIcon={<AddLinkIcon fontSize="small" />}
                      sx={{ textTransform: 'none', backgroundColor: 'var(--wb-primary)', '&:hover': { backgroundColor: 'var(--wb-primary-hover)' } }}
                      onClick={() => setJoinEditor({
                        mode: 'create',
                        initial: { sourceTableId: selection?.type === 'table' ? selection.table.table_id : '', targetTableId: '' }
                      })}>
                      Nueva relación
                    </Button>
                  </Tooltip>
                  <Tooltip title="Volver a acomodar las tablas automáticamente">
                    <Button size="small" variant="outlined" sx={toolbarButtonSx} startIcon={<RefreshIcon fontSize="small" />}
                      onClick={() => applyLayout(graph.nodes, graph.edges, direction)}>
                      Reorganizar
                    </Button>
                  </Tooltip>
                  <Tooltip title="Ver todo el diagrama">
                    <Button size="small" variant="outlined" sx={toolbarButtonSx} startIcon={<CenterFocusStrongIcon fontSize="small" />}
                      onClick={() => fitView({ padding: 0.15, duration: 300 })}>
                      Ajustar vista
                    </Button>
                  </Tooltip>
                </Box>
              </Panel>
            )}
            {graph.missingTableIds.length > 0 && (
              <Panel position="bottom-left">
                <Alert severity="warning" sx={{ py: 0 }}>
                  {graph.missingTableIds.length} tabla(s) del modelo ya no existen y no se muestran.
                </Alert>
              </Panel>
            )}
          </ReactFlow>
        )}
      </Box>
      <Box
        sx={{
          width: { xs: 280, md: 360 },
          flexShrink: 0,
          borderLeft: `1px solid ${BORDER}`,
          backgroundColor: 'var(--wb-bg-panel)',
          overflowY: 'auto'
        }}
        aria-label="Detalle"
      >
        <ModelGraphDetailsPanel
          selection={selection}
          edges={graph.edges}
          externalJoins={graph.externalJoins}
          onClose={() => setSelection(null)}
          onSelectTable={selectTable}
          onSelectEdge={(edge) => setSelection({ type: 'edge', id: edge.id, data: edge.data })}
          onEditJoin={(data) => setJoinEditor({
            mode: 'edit',
            initial: { sourceTableId: data.sourceTable.table_id, targetTableId: data.targetTable.table_id, join: data.join }
          })}
          onDeleteJoin={(data) => setPendingDeleteJoin(data)}
          onEditTable={(table) => setEditingTable(table)}
          onCreateJoinFrom={(table) => setJoinEditor({ mode: 'create', initial: { sourceTableId: table.table_id, targetTableId: '' } })}
        />
      </Box>

      <EntityEditDialog
        table={editingTable}
        onClose={() => setEditingTable(null)}
        onSaved={handleEntitySaved}
        onError={(message) => setNotice({ message, severity: 'error' })}
      />

      <JoinEditorDialog
        open={Boolean(joinEditor)}
        mode={joinEditor?.mode}
        initial={joinEditor?.initial}
        tables={tables}
        existingEdges={graph.edges}
        onClose={() => setJoinEditor(null)}
        onSave={saveJoin}
      />

      {pendingDeleteJoin && (
        <ConfirmDestructiveDialog
          open
          title="Eliminar relación"
          onClose={() => setPendingDeleteJoin(null)}
          onConfirm={deleteJoin}
          progressMessage="Eliminando la relación…"
        >
          <Typography variant="body1">
            ¿Eliminar la relación de <strong>{tableLabel(pendingDeleteJoin.sourceTable)}</strong> hacia{' '}
            <strong>{tableLabel(pendingDeleteJoin.targetTable)}</strong>?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            El asistente dejará de usarla para unir estas tablas en sus consultas.
          </Typography>
        </ConfirmDestructiveDialog>
      )}

      <Snackbar
        open={Boolean(notice)}
        autoHideDuration={4000}
        onClose={() => setNotice(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {notice ? <Alert severity={notice.severity} onClose={() => setNotice(null)}>{notice.message}</Alert> : <span />}
      </Snackbar>
    </Box>
  );
};

/**
 * Diagrama de un modelo semántico: sus tablas y los joins entre ellas,
 * acomodados automáticamente, con el detalle de lo seleccionado a la
 * derecha.
 */
const SemanticModelDiagram = ({ modelId }) => (
  <ReactFlowProvider>
    <DiagramCanvas modelId={modelId} />
  </ReactFlowProvider>
);

export default SemanticModelDiagram;
